<template>
  <div class="page-container">
    <div class="backbtn-container">
      <router-link to="/my-office">
        <div class="back-btn mr-3">
          <v-icon class="pointer-events-none">mdi-arrow-left</v-icon>
        </div>
      </router-link>
      <span class="p-0">{{ this.employeeName }}</span>
    </div>
    <div class="table-container">
      <div class="actions_container">
        <div class="attach-btn-container">
          <v-btn
            style="text-transform: capitalize; font-weight: normal"
            color="primary"
            class="add_product_btn"
            :disabled="!departmentId"
            @click="openAttachProductModel"
            v-if="permissions.OFFICE_PRODUCT_ITEM_ATTACH_USER"
          >
            {{ this.$t("attachProduct") }}
          </v-btn>
        </div>
        <v-btn
          style="text-transform: capitalize; font-weight: normal"
          color="primary"
          class="add_product_btn"
          @click="openAttachDepartmentModel"
          v-if="permissions.DEPARTMENT_ATTACH_USER"
        >
          {{ this.$t("attachDepartmentToUser") }}
        </v-btn>
      </div>
      <div>
        <table-with-filter
          style="width: 100%"
          :is-filterable="false"
          fixed-header
          :height="$vuetify.breakpoint.height - 160"
          hide-default-footer
          :headers="headers"
          :items="employeeProducts"
        >
          <template #item.actions="{ item }">
            <v-btn
              class="new_style"
              @click="$refs.deleteOrderConfirmDialogRef.open(item.id)"
              v-if="permissions.OFFICE_PRODUCT_ITEM_QR_CODE"
            >
              <vue-icon icon="newTrashIcon"></vue-icon>
            </v-btn>
          </template>
        </table-with-filter>
        <VuePagination
          @update:limit="
            (limit) => {
              tableOptions.limit = limit;
              tableOptions.page = 1;
              changePage();
            }
          "
          v-model="tableOptions.page"
          :length="lastPage"
          @input="changePage"
          max-page
          :limit="tableOptions.limit"
        />
      </div>
    </div>
    <v-dialog v-model="attachProductDialog" max-width="400px">
      <div class="model_container">
        <span>
          <span class="title">{{ this.$t("attachProduct") }}</span>
          <div @click="closeModal">
            <vue-icon
              icon="newBoldCloseIcon"
              style="cursor: pointer"
            ></vue-icon>
          </div>
        </span>
        <ValidationObserver ref="observerProduct">
          <form @submit.prevent="attachProductSubmit" style="width: 100%">
            <div class="modalForm">
              <label>
                {{ this.$t("selectProduct") }}
                <ValidationProvider
                  rules="required"
                  name="selectProductPr"
                  v-slot="{ errors }"
                >
                  <vue-select
                    :class="{ errorSelect: !!errors[0] }"
                    ref="ProductSelectRef"
                    :name="$t('selectProduct')"
                    :placeholder="$t('selectProduct')"
                    @item-resolved="setProductList"
                    :get-item-promise="
                      ({ text, page }) =>
                        getProductList({ name: text, page, limit: 15 })
                    "
                    v-model="productId"
                    :items="productList"
                    clearable
                    itemText="name"
                    item-value="productId"
                    class="user_input no-margin"
                    style="flex-grow: 1"
                  ></vue-select>
                </ValidationProvider>
              </label>
              <label>
                {{ this.$t("quantityProducts") }}
                <ValidationProvider
                  :name="`${$t('quantityProducts')}`"
                  rules="required|min_value:1"
                  v-slot="{ errors }"
                  style="
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                  "
                >
                  <input
                    type="number"
                    v-model="count"
                    :class="{ errorInput: !!errors[0] }"
                    placeholder="0"
                  />
                  <span class="error-text">{{ errors[0] }}</span>
                </ValidationProvider>
              </label>
              <v-btn
                type="submit"
                color="primary"
                class="add btn"
                :loading="isLoading"
              >
                {{ this.$t("add") }}
              </v-btn>
            </div>
          </form>
        </ValidationObserver>
      </div>
    </v-dialog>
    <v-dialog v-model="attachDepartmentDialog" max-width="400px">
      <div class="model_container">
        <span>
          <span class="title">{{ this.$t("attachDepartmentToUser") }}</span>
          <div @click="closeDepartmentModal">
            <vue-icon
              icon="newBoldCloseIcon"
              style="cursor: pointer"
            ></vue-icon>
          </div>
        </span>
        <ValidationObserver ref="observer">
          <form @submit.prevent="attachDepartmentSubmit" style="width: 100%">
            <div class="modalForm">
              <label>
                {{ this.$t("department") }}
                <ValidationProvider
                  rules="required"
                  name="categoryProductPr"
                  v-slot="{ errors }"
                >
                  <vue-select
                    :class="{ errorSelect: !!errors[0] }"
                    ref="ProductSelectRef"
                    @CHANGE_VALUE="departmentSelect"
                    :name="$t('department')"
                    :placeholder="$t('department')"
                    @item-resolved="setDepartmentList"
                    :get-item-promise="
                      ({ text, page }) =>
                        getDepartmentList({ name: text, page, limit: 15 })
                    "
                    v-model="departmentId"
                    :items="departmentList"
                    clearable
                    itemText="name"
                    class="user_input no-margin"
                    style="flex-grow: 1"
                  ></vue-select>
                </ValidationProvider>
              </label>
              <v-btn
                type="submit"
                color="primary"
                class="add btn"
                :loading="isLoading"
              >
                {{ this.$t("add") }}
              </v-btn>
            </div>
          </form>
        </ValidationObserver>
      </div>
    </v-dialog>
    <warning-dialog
      v-model="warningDialog"
      @apply="handleDelete"
      ref="deleteOrderConfirmDialogRef"
    ></warning-dialog>
  </div>
</template>

<script>
import TableWithFilter from "@/components/ui/table/TableWithFilter.vue";
import VueSelect from "@/components/ui/vue-select/VueSelect.vue";
import WarningDialog from "@/components/ui/prompt/warning-dialog";
import VuePagination from "@/components/ui/vue-pagination/VuePagination.vue";
import { makerPagination } from "@/utils/pureFunctions";
export default {
  components: { VueSelect, TableWithFilter, WarningDialog, VuePagination },
  data() {
    return {
      lastPage: 0,
      tableOptions: {
        page: 1,
        limit: 10,
      },
      warningDialog: false,
      permissions: {},
      employeeProducts: [],
      attachProductDialog: false,
      attachDepartmentDialog: false,
      departmentList: [],
      productList: [],
      employeeName: null,
      departmentId: null,
      productId: null,
      count: null,
      isLoading: false,
    };
  },
  methods: {
    async changePage() {
      await this.$router.replace({
        query: { ...this.tableOptions },
      });
      await this.getEmployeeProductData();
    },
    async handleDelete(id) {
      try {
        this.showLoading();
        await this.$http.post(`office-product-item/take-from-user/${id}`);
        await this.getEmployeeProductData();
      } catch (e) {
        console.log(e);
      } finally {
        this.clearLoading();
      }
    },
    closeModal() {
      this.attachProductDialog = false;
      this.$refs.observerProduct.reset();
    },
    closeDepartmentModal() {
      this.attachDepartmentDialog = false;
      this.$refs.observer.reset();
    },
    async openAttachProductModel() {
      this.attachProductDialog = true;
    },
    async openAttachDepartmentModel() {
      if (!this.departmentList.length) {
        await this.getDepartmentList({ page: 0, limit: 15 });
      }
      this.attachDepartmentDialog = true;
    },
    attachProductSubmit() {
      this.$refs.observerProduct.validate().then(async (success) => {
        if (success) {
          try {
            this.showLoading();
            await this.$http.post("office-product-item/attach", null, {
              params: {
                departmentId: this.departmentId,
                userId: this.$route.params.employeeIdView,
                productId: this.productId,
                count: this.count,
              },
            });

            await this.getEmployeeProductData();
            this.closeModal();
          } catch (e) {
            console.log(e);
          } finally {
            this.clearLoading();
          }
        }
      });
    },
    async attachDepartmentSubmit() {
      this.$refs.observer.validate().then(async (success) => {
        if (success) {
          const params = {
            userId: this.$route.params.employeeIdView,
            departmentId: this.departmentId,
          };

          try {
            this.showLoading();
            await this.$http.post("deparments/attach-user", null, {
              params: { ...params },
            });
            await this.getEmployeeProductData();
          } catch (e) {
            console.log(e);
          } finally {
            this.clearLoading();
            this.closeDepartmentModal();
          }
        }
      });
    },
    setDepartmentList(data) {
      this.categoryList = data;
    },
    setProductList(data) {
      this.productList = data.items;
    },
    async departmentSelect() {
      await this.getProductList();
    },
    async getDepartmentList(params) {
      try {
        const { data } = await this.$http.get("deparments", {
          params: { ...params },
        });
        this.departmentList = data.data;

        return {
          data: data.data,
        };
      } catch (e) {
        console.log(e);
      }
    },
    async getProductList(params) {
      try {
        const { data } = await this.$http.get(
          `office-product/by-department/${this.departmentId}`,
          {
            params: {
              ...params,
            },
          }
        );
        this.productList = data.data.items;
        return {
          data: data.data.items,
        };
      } catch (e) {
        console.log(e);
      }
    },
    async getEmployeeProductData() {
      // const id = this.$route.params.productIdView;
      const { page, limit, sortBy, sortDirection } = this.tableOptions;
      const { page: queryPage } = this.$route.query;
      const params = {
        page: page - 1,
        limit,
        sortBy,
        sortDirection,
      };
      if (queryPage && +queryPage !== page) {
        await this.$router.replace({
          query: { page: page.toString() },
        });
      }
      try {
        const { data } = await this.$http.get("office-product-item/getAll", {
          params: {
            page: 0,
            limit: 10,
            userId: this.$route.params.employeeIdView,
            ...params,
          },
        });
        this.permissions = data.permissions;
        this.employeeProducts = data.data.items;
        this.employeeName = data.data.user;
        this.departmentId = data.data.departmentId;
        this.lastPage = makerPagination({
          total: data.data.total,
          limit: this.tableOptions.limit,
        }).lastPage;
      } catch (e) {
        console.log(e);
      }
    },
  },
  computed: {
    headers() {
      return [
        {
          text: this.$t("name"),
          value: "name",
        },
        {
          text: this.$t("productCharacteristics"),
          value: "description",
        },
        {
          text: this.$t("codeForInventory"),
          value: "invCode",
        },
        {
          text: this.$t("dataModified"),
          value: "date",
        },
        {
          text: this.$t("action"),
          value: "actions",
        },
      ];
    },
  },
  async created() {
    await this.getEmployeeProductData();
    if (this.departmentId) {
      await this.getProductList({ page: 0, limit: 15 });
    }
  },
};
</script>

<style lang="scss" scoped>
.model_container {
  background-color: #fff;
  padding: 24px;

  span {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .modalForm {
    display: flex;
    flex-direction: column;
    gap: 18px;
    margin-top: 20px;

    input {
      width: 100%;
    }
  }
}

input,
textarea {
  background-color: #fafbfc;
  border-radius: 5px;
  border: 1px solid #dfe2e9;
  outline: none;
  padding: 6px;
}

.errorInput {
  border-color: red;
  -webkit-box-shadow: inset 3px 0px 63px -31px rgba(255, 0, 0, 0.355);
  -moz-box-shadow: inset 3px 0px 63px -31px rgba(255, 0, 0, 0.355);
  box-shadow: inset 3px 0px 63px -31px rgba(255, 0, 0, 0.355);
}

.error-text {
  height: 10px;
  font-size: 12px;
  color: red;
  font-family: "Rubik", sans-serif;
  margin-top: 5px;
}

.backbtn-container {
  display: flex;
  align-items: center;
  gap: 3px;
}

.back-btn {
  background-color: #fff;
  border-radius: 6px;
  display: inline-block;
  padding: 4px;
  transition: 0.4s ease;
  border: 1px solid #dfe2e9;
  box-shadow: 0px 1px 2px 0px #0e31581a;

  &:hover {
    border-color: #0096b2;
  }
}

.actions_container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 24px;

  .generation_box {
    display: flex;
    align-items: center;
    gap: 15px;

    p {
      font-family: "Rubik", sans-serif;
      font-size: 12px;
      font-style: italic;
      font-weight: 400;
      line-height: 14.22px;
      color: #a2a5b9;
    }
  }
}

.table-container {
  background-color: #fff;
  border-radius: 6px;
  padding: 24px;
  margin-top: 24px;
}

.badge {
  border-radius: 6px;
  padding: 8px 10px;
  background-color: #e3e6eb;
  color: #647182;

  &.badgeSuccess {
    background-color: #a2f6a0;
    color: #178015;
  }
}
</style>
